import React, { memo, useState } from 'react';
import { Icon } from '@fieldnation/platform-components';
import type { IconList } from '@fieldnation/platform-components/src/Icon/types.d';
import classes from './ThumbButtonGroup.scss';
import { ThumbButtonProps } from '../types.d';

const BUTTON_CONFIG = {
  positive: {
    activeColor: 'u-color--green-60 u-padding-top--xs',
    hoverColor: 'u-color--green-60 u-padding-top--xs',
    iconSelected: 'thumbUp' as const,
    iconDefault: 'thumbUpOutlined' as const,
    activeClass: classes.thumb_button_positive_active,
  },
  negative: {
    activeColor: 'u-color--orange-60 u-padding-top--xs',
    hoverColor: 'u-color--orange-40 u-padding-top--xs',
    iconSelected: 'thumbDown' as const,
    iconDefault: 'thumbDownOutlined' as const,
    activeClass: classes.thumb_button_negative_active,
  },
};

const ThumbButton = ({
  type,
  isSelected,
  onClick,
  position = 'left',
}: ThumbButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const config = BUTTON_CONFIG[type];
  const defaultColor = 'u-color--gray-60 u-padding-top--xs';

  const getIconColor = () => {
    if (isSelected) return config.activeColor;
    if (isHovered) return config.hoverColor;
    return defaultColor;
  };

  const positionClass = position === 'left' ? classes.thumb_button_left : classes.thumb_button_right;
  const activeClass = isSelected ? config.activeClass : '';

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const iconName = (isSelected ? config.iconSelected : config.iconDefault) as IconList;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`${classes.thumb_button} ${positionClass} ${activeClass}`}
      aria-pressed={isSelected}
      aria-label={type === 'positive' ? 'Thumbs up' : 'Thumbs down'}
    >
      <span className={getIconColor()}>
        <Icon name={iconName} size={18} />
      </span>
    </div>
  );
};

export default memo(ThumbButton);
